import type { CadLock, CadSuggestion, CadTypeaheadResult } from "./api";
import { SUBJECT_FIELD_SCHEMA } from "./fieldSchema";
import { countySubjectToGrid, gridFromSubject } from "./subjectGrid";
import type { SubjectGrid } from "./types";

/**
 * THE COUNTY CAD SUBJECT PICKER'S PURE HALF (slice data-cad-picker, 2026-10-04).
 *
 * Everything here is a function of its arguments, so the rules the owner set can be pinned without
 * a screen: which rows are pickable, what a picked record puts in the grid and under which source
 * tag, which of the agent's own corrections a switch would replace, and what the change ledger
 * records. `SubjectAddressStep` renders; this decides.
 *
 * NOTHING HERE DEFAULTS. A row with no `prop_id` cannot be locked, so it is dropped. A row with no
 * address cannot be told apart from its neighbors, so it is dropped. A missing living area or year
 * is left out of the row's description rather than printed as 0 or "—", and left blank in the grid.
 */

/** The record the subject is locked to. `propId` is the lock, exactly as `clip` is for Cotality. */
/** The roll a record's dataset was loaded from — `cad_picker.roll_for`, sent as the suggestion's
 * `roll`. The stage is the one the load recorded; it is never inferred from a date. */
export interface CadRoll {
  county?: string | null;
  roll_year?: number | null;
  roll_stage?: string | null;
  load_date?: string | null;
  export_date?: string | null;
}

export interface CadPick {
  propId: string;
  datasetVersion: string | null;
  address: string;
  county: string | null;
  /** The short name every tag starts with, e.g. "Collin CAD". */
  cadLabel: string;
  /** The server's record-level tag, "Collin CAD, record 123, dataset v", when it sent one. */
  sourceTag: string | null;
  /** The server's lock, returned as `meta.cad_pick` so the confirm route can re-read this record
   * by prop_id and keep its source tags. Null on a row that did not carry one. */
  lock: CadLock | null;
  /** The record's roll (stage, year, load and export dates); null or absent when none recorded. */
  roll?: CadRoll | null;
  /** The server's why-blank sentences, keyed by grid key (`field_notes`); absent when none. */
  fieldNotes?: Record<string, string>;
  /** The server's `field_states`: a filled field's state tag and note ("inferred unit",
   * "computed"), keyed by grid key; absent when none. */
  fieldStates?: Record<string, CadFieldState>;
  /** The row's details line (`rowDetails`), kept so the picked-record summary repeats exactly what
   * the dropdown showed (owner, 2026-10-19: "garage 440 sq ft" on both). */
  details?: string;
}

/** A filled field's state (2026-10-19): never shown as plain verified. `review` (owner,
 * 2026-10-19): a reading the agent must confirm — a Collin bath split, a Dallas subdivision read
 * from the legal description. The row is prefilled but UNCHECKED, so it is not used or priced
 * until the agent checks it (`gridFromCadRow`). */
export interface CadFieldState {
  state: string;
  note?: string;
  review?: boolean;
}

/** One pickable row, normalized. `raw` is kept so the grid is filled from the server's own record. */
export interface CadRow {
  pick: CadPick;
  city: string | null;
  zip: string | null;
  propertyType: string | null;
  livingArea: number | null;
  yearBuilt: number | null;
  /** The county's garage AREA (sq ft), when it states one above zero; never a bay count. */
  garageSqft: number | null;
  raw: CadSuggestion;
}

/** One entry of the subject change ledger, in the five keys `cad_picker.stamped_subject_change_log`
 * keeps. The SERVER stamps who (`by`) and when it heard (`received_at`); the client never claims to
 * know who. */
export interface SubjectChangeEntry {
  /** Null when the subject being replaced was not a county record (typed, or from Cotality). */
  from_prop_id: string | null;
  from_dataset_version: string | null;
  to_prop_id: string;
  to_dataset_version: string | null;
  /** ISO-8601, the moment the agent confirmed the switch. */
  at: string;
  /** The agent's own corrections the switch replaced. Kept here so a correction is never silently
   * discarded: it leaves the grid, and it stays on record in the saved review and on the server,
   * which stores it (bounded) with the job's inputs when the Generate is ACCEPTED
   * (`cad_picker.stamped_subject_change_log`, `amb_report._accepted_inputs`). Absent when nothing
   * was replaced. */
  replaced_fields?: { key: string; value: unknown; original_value: unknown; original_source: string | null }[];
}

/** The two zero-match states the owner named, plus the rest of `cad_availability`'s vocabulary. */
export type CadZeroState =
  | { kind: "not_in_dataset"; reason: string }
  | { kind: "no_dataset"; reason: string }
  | { kind: "other"; reason: string };

/** What the screen says when the public-records lookup is unavailable (2026-10-17). Calm and
 * vendor-neutral, one sentence each, and never "CAD data is needed": the county records are asked
 * first, so this only ever shows when they had no match to offer. */
export const PUBLIC_RECORDS_UNAVAILABLE =
  "The public-records lookup isn't available right now. You can still enter the subject's " +
  "details yourself or upload the CAD sheet.";

function str(v: unknown): string | null {
  if (typeof v === "string" && v.trim()) return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return null;
}

function num(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return null;
}

/** "Collin CAD": the row's own short name, or "<County> CAD" from its county. Never invented. The
 * district's full name (`cad_label`) is not used for tags — the server's tags start with `cad`, and
 * a tag that differs from the server's by one word is a tag the confirm route will not keep. */
function labelFor(r: CadSuggestion): string | null {
  const cad = str(r.cad);
  if (cad) return cad;
  const county = str(r.county);
  return county ? `${county.replace(/\s+county$/i, "")} CAD` : null;
}

/** The server's `field_notes`, kept only as non-empty strings; undefined when none survive. */
function notesOf(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "string" && v.trim()) out[k] = v.trim();
  }
  return Object.keys(out).length ? out : undefined;
}

/** The server's `field_states`, kept only with a non-empty state; undefined when none survive. */
function statesOf(raw: unknown): Record<string, CadFieldState> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<string, CadFieldState> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v || typeof v !== "object") continue;
    const { state, note, review } = v as { state?: unknown; note?: unknown; review?: unknown };
    if (typeof state !== "string" || !state.trim()) continue;
    out[k] = {
      state: state.trim(),
      ...(typeof note === "string" && note.trim() ? { note: note.trim() } : {}),
      ...(review === true ? { review: true } : {}),
    };
  }
  return Object.keys(out).length ? out : undefined;
}

export function normalizeCadResults(payload: CadTypeaheadResult | null | undefined): CadRow[] {
  const rows = payload?.results;
  if (!Array.isArray(rows)) return [];
  const out: CadRow[] = [];
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const propId = str(r.prop_id);
    const address = str(r.address) ?? str(r.situs);
    const cadLabel = labelFor(r);
    // No lock, no row. No address, no way to tell it apart. No label, no honest source tag.
    if (!propId || !address || !cadLabel) continue;
    out.push({
      pick: {
        propId,
        datasetVersion: str(r.dataset_version),
        address,
        county: str(r.county),
        cadLabel,
        sourceTag: str(r.source_tag),
        lock: r.lock && typeof r.lock === "object" ? { ...r.lock } : null,
        // Present only when the server sent one: a row without a roll is unchanged in shape.
        ...(rollOf((r as { roll?: unknown }).roll) ? { roll: rollOf((r as { roll?: unknown }).roll) } : {}),
        ...(notesOf(r.field_notes) ? { fieldNotes: notesOf(r.field_notes) } : {}),
        ...(statesOf(r.field_states) ? { fieldStates: statesOf(r.field_states) } : {}),
      },
      city: str(r.city),
      zip: str(r.zip),
      // The labelled type, or the CAD's own code when the server cannot label it — never guessed.
      propertyType: str(r.property_type) ?? str(r.property_type_code),
      livingArea: num(r.living_area_sqft),
      yearBuilt: num(r.year_built),
      garageSqft: garageArea(r),
      raw: r,
    });
  }
  for (const row of out) row.pick.details = rowDetails(row);
  return out.slice(0, 10);
}

/** The county's garage AREA from the served subject (`garage_sqft`), or null. A 0 or a missing
 * figure is null: "garage 0" is never printed (owner, 2026-10-19). */
function garageArea(r: CadSuggestion): number | null {
  const v = num((r.subject as Record<string, unknown> | undefined)?.garage_sqft);
  return v !== null && v > 0 ? v : null;
}

/** The second line of a row: what tells two "100 Main St" records apart. Only stated facts. */
export function rowDetails(row: CadRow): string {
  const place = [row.city, row.zip].filter(Boolean).join(" ");
  const parts = [
    place || null,
    row.propertyType,
    row.livingArea !== null ? `${row.livingArea.toLocaleString("en-US")} sq ft` : null,
    row.raw.living_area_flagged ? "living area flagged" : null,
    row.yearBuilt !== null ? `built ${row.yearBuilt}` : null,
    row.garageSqft != null ? `garage ${row.garageSqft.toLocaleString("en-US")} sq ft` : null,
  ].filter((p): p is string => !!p);
  return parts.join(" · ");
}

/** What the screen says when a county record's living area was withheld from GLA (an implausible
 * figure, `core/plausibility.py`). Null when nothing was withheld. Read off the server's own flag. */
export function livingAreaFlagNote(flagged: unknown): string | null {
  if (!flagged || typeof flagged !== "object") return null;
  const f = flagged as { value?: unknown; reason?: unknown };
  const value = num(f.value);
  const reason = str(f.reason);
  if (value === null || !reason) return null;
  return `Living area not filled: the county record states ${value.toLocaleString("en-US")} sq ft. ${reason}`;
}

function rollOf(v: unknown): CadRoll | null {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  return {
    county: str(r.county),
    roll_year: num(r.roll_year),
    roll_stage: str(r.roll_stage),
    load_date: str(r.load_date),
    export_date: str(r.export_date),
  };
}

const STATED_STAGES = ["preliminary", "certified", "supplement"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-04" -> "4 Oct 2026"; anything else printed as given (`cad_picker._day_month_year`). */
function dayMonthYear(iso: string | null | undefined): string | null {
  const text = (iso ?? "").trim();
  if (!text) return null;
  const m = /^(\d+)-(\d+)-(\d+)/.exec(text.slice(0, 10));
  if (!m) return text;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (!(mo >= 1 && mo <= 12)) return text;
  return `${d} ${MONTHS[mo - 1]} ${y}`;
}

/** `cad_picker.roll_phrase`'s twin: "Collin County 2026 preliminary roll, loaded 4 Oct 2026" /
 * "Dallas County 2026 roll, roll stage not stated, export dated 2026-06-01". No stage recorded,
 * no phrase — a stage is never inferred from a date. */
export function rollPhrase(roll: CadRoll | null | undefined): string | null {
  if (!roll || !roll.roll_stage) return null;
  const county = (roll.county ?? "").trim();
  const head = [county ? `${county} County` : "", roll.roll_year ? String(roll.roll_year) : ""]
    .filter(Boolean).join(" ");
  const stage = roll.roll_stage.trim().toLowerCase();
  const loaded = dayMonthYear(roll.load_date);
  if (STATED_STAGES.includes(stage)) {
    const out = `${head} ${stage} roll`.trim();
    return loaded ? `${out}, loaded ${loaded}` : out;
  }
  const out = `${head} roll, roll stage not stated`.trim();
  const exp = (roll.export_date ?? "").trim();
  if (exp) return `${out}, export dated ${exp}`;
  return loaded ? `${out}, loaded ${loaded}` : out;
}

/** The record-level tag: "Collin CAD, Collin County 2026 preliminary roll, loaded 4 Oct 2026,
 * record 123, dataset v". The server's own string when it sent one; otherwise built in exactly
 * `ingestion.core.provenance.cad_record_tag`'s format (", "-joined parts — changed 2026-10-04 by
 * LVS from the earlier " · " separator; `client/src/cma/fixtures/cadRecordTag.json`'s `_about`
 * documents the change), so the two can never read differently. THE ONE CLIENT BUILDER: both
 * builders are pinned to one shared fixture (`fixtures/cadRecordTag.json`, `cases` and
 * `roll_cases`), so adding a part is one change per side. */
export function cadRecordTag(pick: CadPick): string {
  if (pick.sourceTag) return pick.sourceTag;
  const parts = [pick.cadLabel];
  const phrase = rollPhrase(pick.roll);
  if (phrase) parts.push(phrase);
  parts.push(`record ${pick.propId}`);
  if (pick.datasetVersion) parts.push(`dataset ${pick.datasetVersion}`);
  return parts.join(", ");
}

function fieldTag(row: CadRow, key: string): string {
  const fs = row.raw.field_sources?.[key];
  if (typeof fs === "string" && fs.trim()) return fs.trim();
  // The field came in this record, from this dataset, with no finer attribution. Its tag is the
  // record's — the true statement, never a stronger one.
  return cadRecordTag(row.pick);
}

/** The subject grid a picked record produces. The address is the record's own situs.
 *
 * A row whose state needs review (`CadFieldState.review`) is prefilled but left UNCHECKED: only
 * checked rows build the subject (`CmaAmbFlow`'s payload loop), so the value is shown and tagged
 * but neither used nor priced until the agent checks it — the same confirm path as any row. */
export function gridFromCadRow(row: CadRow): SubjectGrid {
  const subject = countySubjectToGrid({
    ...(row.livingArea !== null ? { gla: row.livingArea } : {}),
    ...(row.yearBuilt !== null ? { year: row.yearBuilt } : {}),
    ...(row.raw.subject ?? {}),
    address: row.pick.address,
  });
  const grid = gridFromSubject(subject, (key) => fieldTag(row, key));
  for (const [key, st] of Object.entries(row.pick.fieldStates ?? {})) {
    if (st.review && grid[key] && grid[key].value !== null) grid[key] = { ...grid[key], checked: false };
  }
  return grid;
}

/** Does this picked row still wait on the agent's review? True while it carries the record's
 * own tag, a review state, and is unchecked. Checking it, or editing it, ends the review. */
export function awaitingReview(pick: CadPick | null | undefined, key: string,
                               field: { checked: boolean; edited?: boolean; sourceTag: string | null } | undefined): boolean {
  if (!pick || !field || field.checked || field.edited) return false;
  if (!field.sourceTag?.startsWith(pick.cadLabel)) return false;
  return !!pick.fieldStates?.[key]?.review;
}

/** The agent's own work in a grid: a value they corrected, or one they typed. The address is not
 * listed — choosing a different record IS choosing a different address. */
export function handEditedKeys(grid: SubjectGrid | null | undefined): string[] {
  if (!grid) return [];
  return SUBJECT_FIELD_SCHEMA.filter((def) => {
    if (def.key === "address") return false;
    const f = grid[def.key];
    if (!f || f.value === null || f.value === undefined || f.value === "") return false;
    return !!f.edited || f.sourceTag === "You entered";
  }).map((def) => def.key);
}

export function fieldLabel(key: string): string {
  return SUBJECT_FIELD_SCHEMA.find((d) => d.key === key)?.label ?? key;
}

/** The ledger entry for a confirmed switch (or a first pick that replaced the agent's own work). */
export function changeEntry(
  from: CadPick | null,
  to: CadPick,
  replacedGrid: SubjectGrid | null | undefined,
  replacedKeys: string[],
  at: Date = new Date(),
): SubjectChangeEntry {
  const entry: SubjectChangeEntry = {
    from_prop_id: from ? from.propId : null,
    from_dataset_version: from ? from.datasetVersion : null,
    to_prop_id: to.propId,
    to_dataset_version: to.datasetVersion,
    at: at.toISOString(),
  };
  if (replacedKeys.length && replacedGrid) {
    entry.replaced_fields = replacedKeys.map((key) => {
      const f = replacedGrid[key];
      return {
        key,
        value: f?.value ?? null,
        original_value: f?.edited ? (f.originalValue ?? null) : null,
        original_source: f?.edited ? f.sourceTag : null,
      };
    });
  }
  return entry;
}

/** One "Did you mean…" entry: a real county street spelling and what choosing it searches for. */
export interface CadSpelling {
  street: string;
  query: string;
  county: string | null;
}

/** The server's "Did you mean…" spellings, at most five. An entry without a spelling or a query
 * cannot be shown or followed, so it is dropped — never filled in. */
export function didYouMean(payload: CadTypeaheadResult | null | undefined): CadSpelling[] {
  const list = payload?.did_you_mean;
  if (!Array.isArray(list)) return [];
  const out: CadSpelling[] = [];
  for (const d of list) {
    if (!d || typeof d !== "object") continue;
    const street = str(d.street);
    const query = str(d.query);
    if (!street || !query) continue;
    out.push({ street, query, county: str(d.county) });
  }
  return out.slice(0, 5);
}

/** Zero rows is a designed state. Which one it is comes from the server's `reason_code`. */
export function zeroState(payload: CadTypeaheadResult | null | undefined): CadZeroState {
  const code = payload?.reason_code ?? "";
  const reason = str(payload?.reason) ?? "";
  if (code === "not_in_dataset") {
    return { kind: "not_in_dataset", reason };
  }
  if (code === "no_dataset_for_zip") {
    return { kind: "no_dataset", reason };
  }
  return { kind: "other", reason };
}
